import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * The elevation tier (ADR 081). `flat` is the border-only default (unchanged);
 * `raised` carries the static contact shadow; `interactive` opts into the hover
 * step — border deepens to `--border-hover` and the raised shadow fades in on
 * the motion namespace, a colour/elevation step with **zero transform** (per the
 * discrete-vs-surface split in `motion.ts`). Consumers opt in explicitly; the
 * default Card renders exactly as before. Exported for the rare surface that
 * can't be a `<div>` (e.g. a `<button>`) but must still wear the same recipe.
 */
const cardVariants = {
	flat: '',
	raised: 'shadow-raised',
	interactive:
		'transition-[box-shadow,border-color] duration-[var(--duration-base)] ease-out hover:border-[var(--border-hover)] hover:shadow-raised',
} as const

export type CardVariant = keyof typeof cardVariants

/**
 * Card family — a surface that promotes the README idiom (`bg-card
 * text-card-foreground rounded-lg border`) so consumers stop hand-rolling
 * card markup. Tokens only (`--card`/`--card-foreground`). Compose the root
 * with the sub-parts: `CardHeader` (holding `CardTitle` + `CardDescription`),
 * `CardContent`, and `CardFooter`. Opt into elevation via {@link CardVariant}.
 */
function Card({
	className,
	variant = 'flat',
	...props
}: React.ComponentProps<'div'> & { variant?: CardVariant }) {
	return (
		<div
			data-slot="card"
			className={cn(
				'bg-card text-card-foreground flex flex-col gap-[var(--space-card)] rounded-lg border py-[var(--space-card)]',
				cardVariants[variant],
				className,
			)}
			{...props}
		/>
	)
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-header"
			className={cn('flex flex-col gap-1.5 px-[var(--space-card)]', className)}
			{...props}
		/>
	)
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-title"
			className={cn('text-h6 leading-none font-semibold', className)}
			{...props}
		/>
	)
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-description"
			className={cn('text-muted-foreground text-body-sm', className)}
			{...props}
		/>
	)
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-content"
			className={cn('px-[var(--space-card)]', className)}
			{...props}
		/>
	)
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="card-footer"
			className={cn('flex items-center px-[var(--space-card)]', className)}
			{...props}
		/>
	)
}

export {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
	cardVariants,
}
